import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Stack, 
  Tooltip, 
  IconButton, 
  Link,
  Chip,
  Collapse,
  Button,
  CircularProgress,
  useTheme
} from '@mui/material';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import moment from 'moment';

// Memoized individual chat item component with minimal re-renders
const ChatItem = memo(({ item, index, openChat, onToggleChat, selectedIndex, originalIndex }) => {
  const theme = useTheme();
  const displayIndex = originalIndex !== undefined ? originalIndex + 1 : index + 1;
  const isSelected = selectedIndex === displayIndex;
  
  // Memoize the toggle handler to prevent re-renders
  const handleToggle = useCallback(() => {
    onToggleChat(item?.id);
  }, [onToggleChat, item?.id]);

  // Memoize expensive calculations - simplified for speed
  const cardStyles = useMemo(() => ({
    marginTop: "24px",
    paddingLeft: 0,
    paddingRight: 0,
    borderRadius: "8px",
    border: `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
    boxShadow: isSelected 
      ? `0px 2px 4px 0px ${theme.palette.primary.main}40`
      : `0px 1px 2px 0px ${theme.palette.divider}`,
    backgroundColor: isSelected ? `${theme.palette.primary.main}08` : theme.palette.background.paper
  }), [isSelected, theme]);

  return (
    <Card
      data-index={displayIndex}
      sx={cardStyles}
    >
      <CardContent
        sx={{
          position: "relative",
          display: "flex",
          justifyContent: "end",
          p: 3,
          '&.MuiCardContent-root:last-child': {
            pb: 2,
          }
        }}
      >
        <Box sx={{ width: "100%" }}>
          <Box sx={{ display: "flex" }}>
            <Box sx={{ width: "100%" }}>
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                sx={{ gap: 3 }}
                alignItems="center"
                spacing={1}
              >
                <Stack
                  direction={"row"}
                  spacing={1}
                  sx={{ rowGap: "8px" }}
                  flexWrap="wrap"
                  alignItems={'center'}
                >
                  <Tooltip
                    title={`Index No: ${displayIndex}`}
                    arrow
                    disableInteractive
                  >
                    <Typography 
                      component={'span'} 
                      sx={{ 
                        fontWeight: 600, 
                        fontSize:'0.875rem',
                        color: isSelected ? '#1976d2' : 'inherit'
                      }}
                    >
                      {displayIndex}.
                    </Typography>
                  </Tooltip>

                  <Tooltip
                    title={`Chat ID: ${item?.id}`}
                    arrow
                  >
                    {item?.id && (
                      <Typography variant={"body1"} mr={1} sx={{fontSize:'0.875rem'}}>
                        <b>ID:</b>{item?.id}
                      </Typography>
                    )}
                  </Tooltip>

                  <Tooltip
                    title={`Rating ${item?.rating}`}
                    arrow
                  >
                    <Box sx={{display: 'flex', alignItems: 'center', fontSize:'0.875rem'}}>
                      ({item?.rating}
                      <StarOutlineIcon
                        sx={{ fontSize: "0.875rem" }}
                        color="primary"
                      />
                      )
                    </Box>
                  </Tooltip>
                  <Typography variant='caption' className='text-muted' sx={{fontStyle:'normal !important', lineHeight:1.5}}>Created at:{moment.unix(item?.created_at).utc().format('MM/DD/YYYY HH:mm')}</Typography>
                <Typography variant='caption' className='text-muted'  sx={{fontStyle:'normal !important', lineHeight:1.5}}>Updated at:{moment.unix(item?.updated_at).utc().format('MM/DD/YYYY HH:mm')}</Typography>
                </Stack>
              </Stack>

              {item?.remark && (
                <Typography sx={{mt:1.5, fontSize:'0.875rem'}}>
                  {item?.remark}
                </Typography>
              )}

              <Box
                direction="row"
                alignItems={"center"}
                sx={{marginTop:2}}
              >
                <Typography 
                  onClick={handleToggle} 
                  sx={{
                    display: 'flex', 
                    alignItems: 'center', 
                    cursor: 'pointer', 
                    fontWeight:600,
                    fontSize:'0.875rem', 
                    marginBottom: (openChat[item?.id] ?? openChat?.expandAll) ? 2 : 0,
                    userSelect: 'none' // Prevent text selection on click
                  }}
                >
                  Parts {(openChat[item?.id] ?? openChat?.expandAll) ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </Typography>
                <Collapse in={openChat[item?.id] ?? openChat?.expandAll ?? false}>
                  {item?.parts && item?.parts.map((part, partIndex) => (
                    <Box 
                      key={partIndex} 
                      sx={{ 
                        marginTop: 1, 
                        padding: "8px, 8px, 2px, 8px", 
                        display:'flex'
                      }}
                    >
                      <Box sx={{display:'flex', alignItems:'flex-start', gap:1}}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            marginBottom: 1, 
                            fontWeight: 'bold', 
                            textTransform: 'capitalize' 
                          }}
                        >
                          {part?.authorType}:
                        </Typography>
                        <Typography variant="body2">{part?.body} <Typography component={'span'} variant='body2' className='text-muted' fontStyle={'normal'}>{moment.unix(part?.created_at).utc().format('MM/DD/YYYY HH:mm')}</Typography></Typography>
                      </Box>
                    </Box>
                  ))}
                </Collapse>
              </Box>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
});

ChatItem.displayName = 'ChatItem';

const ChatDataComponent = memo(({ 
  chatData = [], 
  openChat, 
  setOpenChat, 
  totalCount = 0, 
  selectedIndex
}) => {
  const containerRef = useRef(null);

  const handleOpenchat = useCallback((id) => {
    setOpenChat((prev) => ({...prev, [id]: !prev[id]}));
  }, [setOpenChat]);

  if (!chatData || chatData.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1" color="textSecondary">
          No data to display. Upload a JSON file to get started.
        </Typography>
      </Box>
    );
  }

  return (
    <Box ref={containerRef}>
      {chatData.map((item, index) => (
        <ChatItem
          key={item.originalIndex !== undefined ? item.originalIndex : index}
          item={item}
          index={index}
          openChat={openChat}
          onToggleChat={handleOpenchat}
          selectedIndex={selectedIndex}
          originalIndex={item.originalIndex}
        />
      ))}
    </Box>
  );
});

ChatDataComponent.displayName = 'ChatDataComponent';

export default ChatDataComponent;